import React, { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import SectionHeader from './SectionHeader';
import Input from './Input';
import TileGrid from './TileGrid';
import DeckTile from './DeckTile';
import EmptyState from './EmptyState';
import Skeleton from './Skeleton';
import './DeckPicker.css';

/**
 * Baustein — DeckPicker.
 * Der Körper jeder Deck-Auswahl: Suchfeld (ab DECK_PICKER_SEARCH_THRESHOLD Decks), Gruppen
 * „Virtuelle Decks" / „Physische Decks", Sortierung zuletzt bearbeitet zuerst, Kappung auf zwei
 * Reihen je Gruppe mit „Alle anzeigen", Einzelauswahl per Kachel. Konsumenten: Lobby
 * (`DeckPickerModal`, Rahmen mit Bestätigen-Knopf) und Karten-Modal („Zu Deck hinzufügen").
 *
 * Props:
 *   decks          — Array von { id, name, source, lastEdited, commanderImageUrl, commanderArtist, cardCount } |
 *                    null (lädt → Skeleton-Kacheln)
 *   error          — Error | null → EmptyState variant="error" (Text über errorMessage des Aufrufers
 *                    nicht möglich: ui/ ist Leaf — der Aufrufer reicht `errorText`)
 *   errorText      — Text für den Fehlerzustand (Default „Decks konnten nicht geladen werden.")
 *   onRetry        — optional; rendert „Erneut versuchen" im Fehlerzustand
 *   selectedId     — markierte Deck-ID (aria-pressed an der Kachel)
 *   onSelect(id)   — Klick auf eine Kachel; ein Klick wählt nur aus, bestätigt der Rahmen
 *   disabledIds    — Deck-IDs ohne Klick-Ziel (z.B. Karte bereits im Deck), mit `disabledLabel` als Zusatz
 *   emptyState     — ReactNode, wenn decks.length === 0
 *   hint           — optionaler Hinweistext über den Gruppen
 *   autoFocusSearch — Suchfeld beim Rendern fokussieren (Modale)
 */
export const DECK_PICKER_SEARCH_THRESHOLD = 8;

// Deck-Liste, Lobby und Karten-Modal lesen die Quelle gleich: fehlt sie, gilt das Deck als virtuell.
export const toSource = (d) => String(d.source || '').toLowerCase();

export const byLastEditedDesc = (a, b) => {
  const av = a.lastEdited ? new Date(a.lastEdited).getTime() : null;
  const bv = b.lastEdited ? new Date(b.lastEdited).getTime() : null;
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return bv - av;
};

export default function DeckPicker({
  decks, error = null, errorText = 'Decks konnten nicht geladen werden.', onRetry,
  selectedId = null, onSelect, disabledIds = [], disabledLabel, emptyState, hint, autoFocusSearch = false,
}) {
  const [search, setSearch] = useState('');
  const disabled = useMemo(() => new Set(disabledIds), [disabledIds]);

  const groups = useMemo(() => {
    if (!decks) return [];
    const query = search.trim().toLowerCase();
    const visible = decks.filter((d) => (d.name || '').toLowerCase().includes(query));
    return [
      { key: 'virtual', label: 'Virtuelle Decks', items: visible.filter((d) => toSource(d) === 'virtual' || toSource(d) === '') },
      { key: 'physical', label: 'Physische Decks', items: visible.filter((d) => toSource(d) === 'physical') },
    ]
      .map((g) => ({ ...g, items: [...g.items].sort(byLastEditedDesc) }))
      .filter((g) => g.items.length > 0);
  }, [decks, search]);

  if (error) {
    return (
      <EmptyState
        variant="error"
        icon={Layers}
        title={errorText}
        action={onRetry ? <button type="button" className="btn-secondary" onClick={onRetry}>Erneut versuchen</button> : undefined}
      />
    );
  }
  if (decks === null) {
    return (
      <TileGrid>
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} width="100%" height={210} radius="lg" label="Decks werden geladen" />)}
      </TileGrid>
    );
  }
  if (decks.length === 0) return <div className="deck-picker__empty">{emptyState}</div>;

  const showSearch = decks.length >= DECK_PICKER_SEARCH_THRESHOLD;

  return (
    <div className="deck-picker">
      {showSearch && (
        <div className="deck-picker__search">
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder="Deck suchen …"
            aria-label="Deck suchen"
            autoFocus={autoFocusSearch}
          />
        </div>
      )}
      {hint && <p className="deck-picker__hint">{hint}</p>}
      {groups.length === 0 ? (
        <EmptyState variant="filter" icon={Layers} title="Kein Deck passt zu deiner Suche" />
      ) : groups.map((g) => (
        <section key={g.key} className="deck-picker__group">
          <SectionHeader>{g.label}</SectionHeader>
          <TileGrid collapsedRows={2} showMoreLabel={(h, t) => `Alle ${t} anzeigen`}>
            {g.items.map((d) => {
              const name = d.name || 'Unbenanntes Deck';
              const isDisabled = disabled.has(d.id);
              return (
                <DeckTile
                  key={d.id}
                  image={d.commanderImageUrl || undefined}
                  artist={d.commanderArtist || undefined}
                  name={name}
                  count={d.cardCount}
                  disabled={isDisabled}
                  disabledLabel={disabledLabel}
                  selected={selectedId === d.id}
                  onClick={() => onSelect?.(d.id)}
                  clickLabel={`${name} wählen`}
                />
              );
            })}
          </TileGrid>
        </section>
      ))}
    </div>
  );
}
